// The completable, documentable capability surface for completion and
// hover, built by calling this repo's own real capability factories
// (`capabilities.ts`'s `createStudioCapabilities`) rather than hand-listing
// their member names a second time - the same "never reimplemented"
// discipline `capabilities.ts`'s own header comment already applies to
// `standardCapabilities`. If `entitiesCapability` ever grows a member or
// `STUDIO_CAPABILITY_NAMES` ever changes, this catalog picks it up for free,
// with no risk of drifting stale the way a hand-copied member list would.
//
// The handles passed in are never called - `createStudioCapabilities` only
// needs them to build each capability's real `type` (a `functionType`/
// `recordType` value, per `@hollow/script`'s own `CapabilityBinding` shape),
// which is all this module reads. Each stub method's body is unreachable
// from a pure introspection pass; they exist only so `StudioCapabilityHandles`
// type-checks.

import type { HostCapabilities, Type } from '@hollow/script';
import {
  createStudioCapabilities,
  narrowGrantedCapabilities as narrowGrantedCapabilitiesImpl,
  STUDIO_CAPABILITY_NAMES,
  type StudioCapabilityHandles,
  type StudioCapabilityName,
} from '../capabilities.js';
import { functionSignatureText, runtimeTypeToString } from './typeText.js';

const UNREACHABLE_STUB_HANDLES: StudioCapabilityHandles = {
  liveScene: {
    spawn: () => '',
    getInstance: () => undefined,
    setPosition: () => true,
    remove: () => true,
  },
  logic: {
    variable: () => 0,
    setVariable: () => undefined,
    applyAction: () => undefined,
  },
  audio: {
    playOneShot: () => true,
  },
  // `input` is the one optional handle on `StudioCapabilityHandles` (its own
  // doc comment: omitting it just leaves `uses input` ungranted at runtime).
  // Supplied here anyway - completion/hover should show every real v1
  // capability regardless of whether a particular session has actually
  // wired a live handle for it yet, the same posture already taken for the
  // other four (DockConsole shows them in completion even with no live
  // Preview session running).
  input: {
    isActionDown: () => true,
    moveAxis: () => ({ x: 0, y: 0 }),
  },
  output: { writeLine: () => undefined },
  services: { now: () => '', today: () => '', randomFloat: () => 0 },
};

/** Every v1 capability's real `HostCapabilities` entry (name -> `{ type,
 * value }`), granted in full - the same shape `check`/`run` consume,
 * introspected purely for its `type`. Computed once at module load: pure,
 * cheap (no invocation, no I/O), and safe to share across every editor
 * instance and every project's differently-narrowed grant. */
export const HOLLOWSCRIPT_CAPABILITY_CATALOG: HostCapabilities = createStudioCapabilities(UNREACHABLE_STUB_HANDLES);

export interface CapabilityMember {
  readonly name: string;
  readonly signature: string;
  readonly type: Type;
}

/** `entities` -> [`spawn(String, Int, Float, Float, Float): String`, ...],
 * read straight off the catalog's real `RecordType` fields - a capability
 * bound directly to a bare function (none exist in v1, but the shape
 * supports it) reports zero members, not a crash. */
export function capabilityMembers(capabilityName: string): readonly CapabilityMember[] {
  const binding = HOLLOWSCRIPT_CAPABILITY_CATALOG.get(capabilityName);
  if (binding === undefined || binding.type.kind !== 'Record') return [];
  return [...binding.type.fields.entries()].map(([name, type]) => ({
    name,
    signature: functionSignatureText(name, type),
    type,
  }));
}

/** A one-line hover/completion-detail description of a capability name
 * itself (e.g. `entities`), before drilling into a member. */
export function capabilityNamespaceDetail(capabilityName: string): string | undefined {
  const binding = HOLLOWSCRIPT_CAPABILITY_CATALOG.get(capabilityName);
  if (binding === undefined) return undefined;
  // A capability bound straight to a function (`print`) reads as its call
  // signature; a namespace reads as its record of functions.
  if (binding.type.kind === 'Function') return `capability · ${functionSignatureText(capabilityName, binding.type)}`;
  return `capability · ${runtimeTypeToString(binding.type)}`;
}

/** A short, human description of what each capability is for - shown in
 * the Code workspace's Reference panel and completion detail, next to the
 * real signature. Kept here, beside the catalog it describes, so a new
 * capability added to `capabilities.ts` fails to build until it has a
 * description too. */
export const CAPABILITY_DESCRIPTIONS: Readonly<Record<StudioCapabilityName, string>> = {
  print: 'Write a line to the Output panel (or the console in Preview).',
  clock: 'Read the current time and date.',
  random: 'Random integers and floats.',
  entities: 'Spawn, read, move and remove objects in the running scene.',
  variables: 'Read and write the Logic variables your rules use.',
  audio: 'Play a one-shot sound through the scene\'s audio engine.',
  logic: 'Trigger the same effects a Logic rule can: announce, quests, items, win or lose.',
  input: 'Read which player actions are held down right now.',
};

/** The one narrowing rule for a project's declared grant, re-exported from
 * `capabilities.ts` so the editor's completion/hover/diagnostics read the
 * exact same rule the runtime applies (`print`/`clock`/`random` always
 * granted, engine-bound names narrowed to the declared subset). */
export const narrowGrantedCapabilities = narrowGrantedCapabilitiesImpl;

/** The capability names actually offered right now: every v1 name when
 * nothing has narrowed the grant, else the narrowed set intersected with
 * `STUDIO_CAPABILITY_NAMES` (already guaranteed by `narrowGrantedCapabilities`,
 * intersected again here defensively since callers may pass a set built by
 * other means). */
export function grantedCapabilityNames(granted: ReadonlySet<StudioCapabilityName> | undefined): readonly StudioCapabilityName[] {
  if (granted === undefined) return STUDIO_CAPABILITY_NAMES;
  return STUDIO_CAPABILITY_NAMES.filter((name) => granted.has(name));
}
